// Pure rate-editor logic for the Plan Builder (PlanBuilderForm): row
// de-duplication/ordering and the CSV rate import (parse → map onto the
// editor's rows). Kept free of React so it is unit-tested directly.
import { TABLE_RATE_LABELS } from "@shared/constants/coverageTable";
import { AGE_BAND_PRESETS } from "./ageBands";

export interface PlanRate {
  id?: number;
  rateStructureType: string;
  coverageTier?: string | null;
  ageBand?: string | null;
  ageBandType?: string | null;
  ratingAreaId?: number | null;
  ratingAreaLabel?: string | null;
  coverageTable?: string | null;
  monthlyRate: string;
}

export const COVERAGE_TIERS = [
  { value: "employee_only", label: "Employee Only" },
  { value: "employee_spouse", label: "Employee + Spouse" },
  { value: "employee_children", label: "Employee + Child(ren)" },
  { value: "family", label: "Family" },
];

/**
 * Structures whose rows can carry a rating area. A plan priced per area keeps
 * one full set of rows per area; area-less rows of the same structure are
 * stale leftovers of a pre-region save. Tier Rates joined in DEV-324.
 */
export const AREA_CAPABLE_STRUCTURES = new Set(["age_rates", "age_tier_rates", "table_rates", "tier_rates"]);

// One row per rate cell, ordered (area, numeric band start, tier): duplicates
// from older multi-version loads collapse (last wins) and "64+, 40, 41, 1"-
// style mixed ordering can't happen in the editor.
export function ageBandStart(band: string | null | undefined): number {
  const n = parseInt(String(band ?? "").split("-")[0].replace("+", ""), 10);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

const tierOrder = (tier: string | null | undefined): number => {
  const i = COVERAGE_TIERS.findIndex((t) => t.value === tier);
  return i === -1 ? COVERAGE_TIERS.length : i;
};

export function dedupeAndSortRates(rows: PlanRate[]): PlanRate[] {
  const byKey = new Map<string, PlanRate>();
  for (const r of rows) {
    const key = [r.rateStructureType, r.coverageTier ?? "", r.ageBand ?? "", r.ratingAreaId ?? "", r.coverageTable ?? ""].join("|");
    byKey.set(key, r); // last occurrence wins
  }
  let out = Array.from(byKey.values());
  // Once a plan prices per rating area, area-less rows of that SAME
  // structure are stale artifacts of pre-region saves — drop them so they
  // stop being re-saved.
  const structuresWithRegions = new Set(
    out.filter((r) => AREA_CAPABLE_STRUCTURES.has(r.rateStructureType) && r.ratingAreaId != null).map((r) => r.rateStructureType),
  );
  if (structuresWithRegions.size > 0) {
    out = out.filter((r) => !structuresWithRegions.has(r.rateStructureType) || r.ratingAreaId != null);
  }
  return out.sort((a, b) => {
    const area = Number(a.ratingAreaId ?? -1) - Number(b.ratingAreaId ?? -1);
    if (area !== 0) return area;
    const band = ageBandStart(a.ageBand) - ageBandStart(b.ageBand);
    if (band !== 0) return band;
    return tierOrder(a.coverageTier) - tierOrder(b.coverageTier);
  });
}

// ---- CSV rate import -------------------------------------------------------
// One plan per file. Carrier rate sheets vary, so header matching is fuzzy:
//   rating_area | area | region        (optional — omit for single-region plans)
//   age_band | age | band              (age-rated rows, e.g. "0-14", "35", "64+")
//   coverage_tier | tier               (tier-rated rows, value or label)
//   coverage_table | table | label     (table-rated rows — one of the 8 fixed labels)
//   monthly_rate | rate | premium | amount
// Rates tolerate "$1,234.56" formatting. A file with BOTH an age and a tier
// column is the age_tier_rates shape (one row per band × tier); a
// coverage_table column is its own shape and excludes age/tier entirely.

export interface CsvRateRow {
  ratingArea: number | null;
  ageBand: string | null;
  coverageTier: string | null;
  coverageTable: string | null;
  monthlyRate: string;
}

export interface ParsedRateCsv {
  rows: CsvRateRow[];
  /** Rows that could not be read (shown in red). */
  errors: string[];
  /** Data the file carries that the import will NOT use (shown in amber). */
  warnings: string[];
}

// Blank downloadable templates (no rate values — fill and re-upload).
// Per-single-age rows 0..63 + "64+", the granularity carriers file at.
const TEMPLATE_AGE_BANDS = [...Array.from({ length: 64 }, (_, age) => String(age)), "64+"];

// Long layout: one row per (rating_area, age_band). Duplicate the block per
// additional rating area.
export const RATE_CSV_TEMPLATE = [
  "rating_area,age_band,monthly_rate",
  ...TEMPLATE_AGE_BANDS.map((band) => `1,${band},`),
].join("\n");

// Wide (Kaiser-style) layout: ages as rows, one column per rating area.
// Leave columns for areas the plan isn't offered in blank.
export const RATE_CSV_TEMPLATE_WIDE = [
  ["Age", ...Array.from({ length: 19 }, (_, i) => `Rating Area ${i + 1}`)].join(","),
  ...TEMPLATE_AGE_BANDS.map((band) => band + ",".repeat(19)),
].join("\n");

// Table Rates: one row per fixed coverage-combination label.
export const RATE_CSV_TEMPLATE_TABLE = [
  "coverage_table,monthly_rate",
  ...TABLE_RATE_LABELS.map((label) => `${label},`),
].join("\n");

export function parseRateCsv(text: string): ParsedRateCsv {
  const errors: string[] = [];
  const warnings: string[] = [];
  const lines = text
    .split(/\r\n|\r|\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return { rows: [], errors: ["CSV needs a header row and at least one data row."], warnings };

  const delimiter = lines[0].includes("\t") ? "\t" : lines[0].includes(";") && !lines[0].includes(",") ? ";" : ",";
  // Quote-aware field split — carrier sheets format rates as "$1,881.64".
  const split = (line: string): string[] => {
    const cells: string[] = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (line[i + 1] === '"') { cur += '"'; i++; }
          else inQuotes = false;
        } else cur += ch;
      } else if (ch === '"') inQuotes = true;
      else if (ch === delimiter) { cells.push(cur); cur = ""; }
      else cur += ch;
    }
    cells.push(cur);
    return cells.map((c) => c.trim());
  };

  const rawHeader = split(lines[0]);
  const header = rawHeader.map((h) => h.toLowerCase().replace(/[\s-]+/g, "_"));
  const findCol = (...names: string[]) => header.findIndex((h) => names.includes(h));
  const areaCol = findCol("rating_area", "area", "region", "rating_region");
  const bandCol = findCol("age_band", "age", "band");
  const tierCol = findCol("coverage_tier", "tier");
  const tableCol = findCol("coverage_table", "table", "label");
  const rateCol = findCol("monthly_rate", "rate", "premium", "amount", "monthly_premium");

  // Any named column the chosen layout doesn't read is reported, so nothing
  // in the file is dropped silently (DEV-324).
  const warnUnused = (used: number[]) => {
    rawHeader.forEach((name, i) => {
      if (!name || used.includes(i)) return;
      warnings.push(`Column "${name}" is not used by this import and will be ignored.`);
    });
  };

  // WIDE layout (Kaiser-style): ages as rows, one column per rating area
  // ("Age, Rating Area 1, Rating Area 2, ..."). Detected by ≥2 area-numbered
  // columns + an age column; pivoted into the same long rows.
  const wideAreaCols: Array<{ index: number; area: number }> = [];
  header.forEach((h, i) => {
    const m = h.match(/^(?:rating_)?(?:area|region)_?(\d+)$/);
    if (m) wideAreaCols.push({ index: i, area: Number(m[1]) });
  });
  if (wideAreaCols.length >= 2 && bandCol >= 0) {
    warnUnused([bandCol, ...wideAreaCols.map((c) => c.index)]);
    const rows: CsvRateRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = split(lines[i]);
      const lineNo = i + 1;
      const band = (cells[bandCol] ?? "").replace(/\s+/g, "");
      if (!/^\d+([-–]\d+)?\+?$/.test(band)) {
        errors.push(`Line ${lineNo}: age band "${cells[bandCol] ?? ""}" not recognized (use "0-14", "35" or "64+").`);
        continue;
      }
      for (const col of wideAreaCols) {
        const rawRate = (cells[col.index] ?? "").replace(/[$,\s]/g, "");
        if (!rawRate) continue; // area not offered for this band — skip silently
        const rate = Number(rawRate);
        if (!Number.isFinite(rate) || rate < 0) {
          errors.push(`Line ${lineNo}: rate "${cells[col.index]}" (area ${col.area}) is not a number.`);
          continue;
        }
        rows.push({
          ratingArea: col.area,
          ageBand: band.replace("–", "-"),
          coverageTier: null,
          coverageTable: null,
          monthlyRate: rate.toFixed(2),
        });
      }
    }
    if (rows.length === 0 && errors.length === 0) errors.push("No data rows found.");
    return { rows, errors, warnings };
  }

  // TABLE layout: no age/tier dimension at all — one row per fixed
  // coverage-combination label ("Employee", "Employee + Spouse", ...).
  if (tableCol >= 0 && bandCol < 0 && tierCol < 0) {
    if (rateCol < 0) {
      return { rows: [], errors: ['No rate column found (expected "monthly_rate", "rate", "premium" or "amount").'], warnings };
    }
    // Table imports load one area-less set; a rating-area column can't be
    // applied here, so say so instead of dropping it silently (DEV-324).
    if (areaCol >= 0) {
      warnings.push(
        `Table Rates imports don't carry rating areas: the "${rawHeader[areaCol]}" column is ignored and every row loads without an area. Use "Add Region" to price Table Rates per area.`,
      );
    }
    warnUnused([tableCol, rateCol, areaCol]);
    const tableByLoose: Record<string, string> = {};
    for (const label of TABLE_RATE_LABELS) {
      tableByLoose[label.toLowerCase().replace(/[^a-z0-9]/g, "")] = label;
    }
    const rows: CsvRateRow[] = [];
    const seenLabels = new Set<string>();
    for (let i = 1; i < lines.length; i++) {
      const cells = split(lines[i]);
      const lineNo = i + 1;
      const rawRate = (cells[rateCol] ?? "").replace(/[$,\s]/g, "");
      const rate = Number(rawRate);
      if (!rawRate || !Number.isFinite(rate) || rate < 0) {
        errors.push(`Line ${lineNo}: rate "${cells[rateCol] ?? ""}" is not a number.`);
        continue;
      }
      const loose = (cells[tableCol] ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const label = tableByLoose[loose];
      if (!label) {
        errors.push(`Line ${lineNo}: coverage label "${cells[tableCol] ?? ""}" not recognized.`);
        continue;
      }
      if (seenLabels.has(label)) errors.push(`Duplicate row for "${label}" — last value wins.`);
      seenLabels.add(label);
      rows.push({ ratingArea: null, ageBand: null, coverageTier: null, coverageTable: label, monthlyRate: rate.toFixed(2) });
    }
    if (rows.length === 0 && errors.length === 0) errors.push("No data rows found.");
    return { rows, errors, warnings };
  }

  if (rateCol < 0) errors.push('No rate column found (expected "monthly_rate", "rate", "premium" or "amount").');
  if (bandCol < 0 && tierCol < 0 && tableCol < 0)
    errors.push('No "age_band" (or "age"), "coverage_tier" (or "tier") or "coverage_table" (or "table"/"label") column found.');
  if (errors.length > 0) return { rows: [], errors, warnings };
  warnUnused([areaCol, bandCol, tierCol, rateCol]);

  const tierByLoose: Record<string, string> = {};
  for (const t of COVERAGE_TIERS) {
    tierByLoose[t.value] = t.value;
    tierByLoose[t.label.toLowerCase().replace(/[^a-z]/g, "")] = t.value;
  }

  const rows: CsvRateRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = split(lines[i]);
    const lineNo = i + 1;
    const rawRate = (cells[rateCol] ?? "").replace(/[$,\s]/g, "");
    const rate = Number(rawRate);
    if (!rawRate || !Number.isFinite(rate) || rate < 0) {
      errors.push(`Line ${lineNo}: rate "${cells[rateCol] ?? ""}" is not a number.`);
      continue;
    }
    let ratingArea: number | null = null;
    if (areaCol >= 0 && cells[areaCol]) {
      const area = Number(String(cells[areaCol]).replace(/[^\d]/g, ""));
      if (!Number.isFinite(area) || area <= 0) {
        errors.push(`Line ${lineNo}: rating area "${cells[areaCol]}" is not a number.`);
        continue;
      }
      ratingArea = area;
    }
    let band: string | null = null;
    if (bandCol >= 0) {
      band = (cells[bandCol] ?? "").replace(/\s+/g, "");
      if (!/^\d+([-–]\d+)?\+?$/.test(band)) {
        errors.push(`Line ${lineNo}: age band "${cells[bandCol] ?? ""}" not recognized (use "0-14", "35" or "64+").`);
        continue;
      }
      band = band.replace("–", "-");
    }
    let tier: string | null = null;
    if (tierCol >= 0) {
      const loose = (cells[tierCol] ?? "").toLowerCase().replace(/[^a-z]/g, "");
      tier = tierByLoose[(cells[tierCol] ?? "").toLowerCase().replace(/[\s-]+/g, "_")] ?? tierByLoose[loose] ?? null;
      if (!tier) {
        errors.push(`Line ${lineNo}: coverage tier "${cells[tierCol] ?? ""}" not recognized.`);
        continue;
      }
    }
    rows.push({ ratingArea, ageBand: band, coverageTier: tier, coverageTable: null, monthlyRate: rate.toFixed(2) });
  }

  // Duplicate cells (same area + band/tier) are a sheet mistake worth flagging.
  const seen = new Set<string>();
  for (const r of rows) {
    const key = `${r.ratingArea ?? ""}|${r.ageBand ?? ""}|${r.coverageTier ?? ""}`;
    if (seen.has(key)) {
      errors.push(
        `Duplicate row for ${r.ageBand ?? r.coverageTier}${r.ratingArea != null ? ` in area ${r.ratingArea}` : ""} — last value wins.`,
      );
    }
    seen.add(key);
  }

  if (rows.length === 0 && errors.length === 0) errors.push("No data rows found.");
  return { rows, errors, warnings };
}

export type CsvImportStructure = "tier_rates" | "age_rates" | "age_tier_rates" | "table_rates";

export interface CsvImportPlan {
  /** Rating Method the import switches the editor to. */
  structure: CsvImportStructure;
  /** The editor's complete row list after the import. */
  rates: PlanRate[];
  /** Data rows read from the file. */
  fileRows: number;
  /** Of those, how many the editor actually keeps (after dedupe). */
  keptRows: number;
  /** Rating areas present in the file, ascending. */
  areas: number[];
  /**
   * The plan already prices this structure per area but the file has no
   * area column — loading it would collapse every area into one set.
   */
  needsAreaColumn: boolean;
}

export function csvImportStructure(rows: CsvRateRow[]): CsvImportStructure {
  if (rows.some((r) => r.ageBand != null && r.coverageTier != null)) return "age_tier_rates";
  if (rows.some((r) => r.coverageTable != null)) return "table_rates";
  if (rows.some((r) => r.coverageTier != null)) return "tier_rates";
  return "age_rates";
}

/**
 * What loading `rows` into an editor holding `current` produces. Pure — the
 * preview reports from it and Load applies `rates` verbatim, so the numbers
 * the user sees are the numbers the editor keeps (DEV-324: the tier branch
 * used to drop the area and the toast counted file rows).
 *
 * Per-area files replace only the areas they list; other areas' rows of the
 * same structure stay. An area-less file replaces the structure's whole set.
 */
export function planCsvImport(rows: CsvRateRow[], current: PlanRate[]): CsvImportPlan {
  const structure = csvImportStructure(rows);
  const areas = Array.from(
    new Set(rows.map((r) => r.ratingArea).filter((a): a is number => a != null)),
  ).sort((a, b) => a - b);
  const importedAreas = new Set(areas);

  // Keep a label someone already gave an area ("CA-15") rather than renaming it.
  const labelByArea = new Map(
    current
      .filter((r) => r.rateStructureType === structure && r.ratingAreaId != null && r.ratingAreaLabel)
      .map((r) => [Number(r.ratingAreaId), r.ratingAreaLabel as string]),
  );
  const areaFields = (area: number | null) =>
    area != null
      ? { ratingAreaId: area, ratingAreaLabel: labelByArea.get(area) || `Area ${area}` }
      : {};

  let imported: PlanRate[];
  if (structure === "age_tier_rates") {
    // Ten-year bands are a distinct, smaller preset — infer from the exact
    // set of bands present rather than defaulting everything to five-year.
    const distinctBands = new Set(rows.map((r) => r.ageBand));
    const tenYear = AGE_BAND_PRESETS.ten_year;
    const bandType = distinctBands.size === tenYear.length && tenYear.every((b) => distinctBands.has(b)) ? "ten_year" : "five_year";
    imported = rows.map((r) => ({
      rateStructureType: "age_tier_rates",
      ageBand: r.ageBand,
      coverageTier: r.coverageTier,
      ageBandType: bandType,
      ...areaFields(r.ratingArea),
      monthlyRate: r.monthlyRate,
    }));
  } else if (structure === "table_rates") {
    imported = rows.map((r) => ({
      rateStructureType: "table_rates",
      coverageTable: r.coverageTable,
      monthlyRate: r.monthlyRate,
    }));
  } else if (structure === "tier_rates") {
    imported = rows.map((r) => ({
      rateStructureType: "tier_rates",
      coverageTier: r.coverageTier,
      ...areaFields(r.ratingArea),
      monthlyRate: r.monthlyRate,
    }));
  } else {
    // Single-age rows anywhere in the file → W&B granularity.
    const bandType = rows.some((r) => /^\d+$/.test(r.ageBand ?? "")) ? "all_ages" : "five_year";
    imported = rows.map((r) => ({
      rateStructureType: "age_rates",
      ageBand: r.ageBand,
      ageBandType: bandType,
      ...areaFields(r.ratingArea),
      monthlyRate: r.monthlyRate,
    }));
  }

  const hasRegionRows = current.some((r) => r.rateStructureType === structure && r.ratingAreaId != null);
  // Table imports never carry areas (see parseRateCsv), so they always
  // replace the whole table set.
  const needsAreaColumn = structure !== "table_rates" && hasRegionRows && rows.length > 0 && areas.length === 0;

  const kept = current.filter((r) => {
    if (r.rateStructureType !== structure) return true;
    if (structure === "table_rates" || importedAreas.size === 0) return false;
    return r.ratingAreaId != null && !importedAreas.has(Number(r.ratingAreaId));
  });
  const rates = dedupeAndSortRates([...kept, ...imported]);
  const importedSet = new Set(imported);
  const keptRows = rates.filter((r) => importedSet.has(r)).length;

  return { structure, rates, fileRows: rows.length, keptRows, areas, needsAreaColumn };
}
